import React, { useState, useEffect, SyntheticEvent, JSX} from "react";
import {
  AppBar,
  Typography,
  Button,
  IconButton,
  TextField,
  Box,
  Breadcrumbs,
  Autocomplete,
  InputAdornment,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { useTheme } from "@mui/material/styles";
import { brandData } from "../../components/grid/GridData.tsx";
import { externalgetApi, externalpostApi } from "../../services/externalApis.tsx";
import { SETTING_API_01, ITEM_API_01 } from "../../api/ApiModel.tsx";
import ViewDataGrid from "../../components/grid/ViewDataGrid.tsx";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import { usePageData } from "../../utils/common/PageDataContext.tsx";
import { CON_STATUS_ACTIVE, CON_STATUS_DELETED, CON_VALUE_ZERO } from "../../utils/sys/Constants.tsx";
import { useSelector } from "react-redux";
import DeleteIcon from "@mui/icons-material/Delete";
import SystemAlert from "../../components/common/SystemAlert.tsx";
// Import messages
import { 
  Messages,
  UITextMessages,
  MessageCategory,
} from "../../utils/sys/Messages.tsx";

// Type Definitions
interface Brand {
  brandcode: string | number;
  brandname: string;
  descrip?: string;
  sts?: string;
  enuser?: string;
  endate?: string;
  [key: string]: any;
}

interface Item {
  itemcode: number;
  itemname: string;
  brandcode: number;
  brandname: string;
  [key: string]: any;
}

interface UserParams {
  LOCCODE?: string;
  STAFFMAPLOCODE?: string;
  COMPCODE?: string;
  USERNAME?: string;
}

interface UserState {
  USER_PARAM?: UserParams;
}

interface MenuItem {
  text: string;
  icon: JSX.Element;
  onClick: (id: string | number) => void;
}

interface ViewDataGridRow extends Brand {
  id: string | number;
}

interface DeletePayload {
  brandcode: string | number;
  compcode: string;
  loccode: string;
  brandname: string;
  descrip: string;
  sts: string;
  enuser: string;
}

interface ApiResponse {
  success?: boolean;
  message?: string;
}

interface ApiError {
  response?: {
    status?: number;
    data?: string | { message?: string; error?: string };
  };
  request?: unknown;
  message?: string;
}

interface PageData {
  title: string;
  [key: string]: unknown;
}

// Define the styles interface
interface Styles {
  box_breadcrumb_view: any;
  breadcrumb: any;
  breadcrumb_link: any;
  breadcrumb_link_page: any;
  box_page_container: any;
  appbar: any;
  appbar_grid_container: any;
  appbar_grid_container_col_1: any;
  appbar_title: any;
  appbar_grid_search: any;
  ipt_appbar_search_auto: any;
  ipt_appbar_search: any;
  btn_appbar_search_icon: any;
  btn_primary: any;
  btn_appbar_add: any;
  view_page_container: any;
  btn_secondary: any;
  btn_add_cancel: any;
  btn_add_confirm: any;
}

function ViewBrands(): JSX.Element {
  const theme = useTheme();
  const styles = useStyles(theme) as Styles;
  const navigate = useNavigate();
  const { setPageData } = usePageData();
  
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<MessageCategory.SUCCESS | MessageCategory.ERROR | MessageCategory.WARNING>(MessageCategory.SUCCESS);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | number | null>(null);
  const [isCheckingUsage, setIsCheckingUsage] = useState<boolean>(false);
  
  const SYS_USER = useSelector((state: { user: UserState }) => state.user) || {};
  const { COMPCODE, STAFFMAPLOCODE, USERNAME } = SYS_USER.USER_PARAM || {};
  
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<Brand[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [selectedItem, setSelectedItem] = useState<Brand | null>(null);
  const [filteredRows, setFilteredRows] = useState<Brand[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");

  useEffect(() => {
    setPageData({
      title: "Brand",
    });
  }, [setPageData]);

  // Function to check if brand is used in any items
  const checkBrandUsage = async (brandcode: string | number): Promise<boolean> => {
    try {
      setIsCheckingUsage(true);
      
      // Fetch items with the same brandcode
      const items = await externalgetApi(ITEM_API_01, "get", {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        STS: CON_STATUS_ACTIVE,
      });

      // Check if any items use this brand
      const isUsed = items?.some((item: Item) => 
        Number(item.brandcode) === Number(brandcode)
      ) || false;

      return isUsed;
    } catch (error) {
      console.error("Error checking brand usage:", error);
      return false;
    } finally {
      setIsCheckingUsage(false);
    }
  };

  const handleItemClick = (id: string | number, actionText: string): void => {
    if (actionText === "Edit") {
      const selectedItem = data?.find((item) => item.brandcode === id);
      if (selectedItem) {
        navigate(`/CreateBrands`, { state: { selectedItem, actionText } });
      }
    }

    if (actionText === "Delete") {
      const item = data?.find((row) => row.brandcode === id);
      setSelectedId(id);
      setSelectedItem(item || null);
      setOpenDeleteDialog(true); // Open confirmation dialog immediately
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (!selectedItem) return;

    try {
      setIsLoading(true);
      
      // Check if brand is used in any items
      const isUsed = await checkBrandUsage(selectedItem.brandcode);
      
      // If brand is in use, show error message and don't delete
      if (isUsed) {
        setAlertMessage(Messages.brand.error.deleteInUse);
        setAlertType(MessageCategory.ERROR);
        setAlertOpen(true);
        setOpenDeleteDialog(false);
        setSelectedId(null);
        setSelectedItem(null);
        return;
      }

      // Validate required parameters
      if (!COMPCODE) {
        throw new Error("Company code is missing");
      }
      if (!STAFFMAPLOCODE) {
        throw new Error("Location code is missing");
      }
      if (!USERNAME) {
        throw new Error("Username is missing");
      }

      const deletePayload: DeletePayload = {
        brandcode: selectedItem.brandcode || CON_VALUE_ZERO,
        compcode: COMPCODE || "",
        loccode: STAFFMAPLOCODE || "",
        brandname: selectedItem.brandname || "",
        descrip: selectedItem.descrip || "",
        sts: CON_STATUS_DELETED || "D",
        enuser: USERNAME || "Admin",
      };

      console.log("DELETE payload:", deletePayload);
      console.log("DELETE URL:", SETTING_API_01);

      const response = await externalpostApi(SETTING_API_01, deletePayload) as ApiResponse;

      console.log("DELETE response:", response);

      if (response && response.success !== false) {
        setData((prev) =>
          prev ? prev.filter((row) => row.brandcode !== selectedItem.brandcode) : []
        );
        setFilteredRows((prev) =>
          prev.filter((row) => row.brandcode !== selectedItem.brandcode)
        );

        // Use brand delete success message
        setAlertMessage(Messages.brand.success.delete);
        setAlertType(MessageCategory.SUCCESS);
        setAlertOpen(true);
        setOpenDeleteDialog(false);
        setSelectedId(null);
        setSelectedItem(null);
      } else {
        throw new Error(response?.message || Messages.brand.error.deleteFailed);
      }
    } catch (error) {
      const err = error as ApiError;
      console.error("DELETE API error:", err);

      // Error handling
      let errorMessage = Messages.brand.error.deleteFailed;

      if (err.response) {
        console.error("Response Status:", err.response.status);
        console.error("Response Data:", err.response.data);

        if (err.response.data) {
          if (typeof err.response.data === "string") {
            if (err.response.data.includes("Object reference not set to an instance of an object")) {
              errorMessage = "Null reference error occurred";
            } else if (err.response.data.trim() !== "") {
              errorMessage = err.response.data;
            }
          } else if ((err.response.data as any).message) {
            errorMessage = (err.response.data as any).message;
          } else if ((err.response.data as any).error) {
            errorMessage = (err.response.data as any).error;
          }
        }

        // Handle specific HTTP status codes
        switch (err.response.status) {
          case 400:
            errorMessage = errorMessage || "Bad request";
            break;
          case 404:
            errorMessage = "Resource not found";
            break;
          case 405:
            errorMessage = "Method not allowed";
            break;
          case 500:
            errorMessage = errorMessage || "Server error";
            break;
          default:
            errorMessage = `${errorMessage || Messages.brand.error.deleteFailed} (Status: ${err.response.status})`;
        }
      } else if (err.request) {
        errorMessage = "No response from server";
      } else {
        errorMessage = err.message || errorMessage;
      }

      setAlertMessage(errorMessage);
      setAlertType(MessageCategory.ERROR);
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelDelete = (): void => {
    setOpenDeleteDialog(false);
    setSelectedId(null);
    setSelectedItem(null);
  };

  const menuItems: MenuItem[] = [
    {
      text: "Edit",
      icon: <BorderColorOutlinedIcon />,
      onClick: (brandcode: string | number) => handleItemClick(brandcode, "Edit"),
    },
    {
      text: "Delete",
      icon: <DeleteIcon />,
      onClick: (brandcode: string | number) => handleItemClick(brandcode, "Delete"),
    },
  ];

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const result = await externalgetApi(SETTING_API_01, "get", {
          COMPCODE,
          LOCCODE: STAFFMAPLOCODE,
          STS: CON_STATUS_ACTIVE,
        });

        setData(result);
        setFilteredRows(result);
        console.log("result", result);
      } catch (err: any) {
        setError(err);
        setAlertMessage(Messages.brand.error.fetchFailed);
        setAlertType(MessageCategory.ERROR);
        setAlertOpen(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [COMPCODE, STAFFMAPLOCODE]);

  const handleSearchChange = (event: SyntheticEvent, value: string | null): void => {
    const searchValue = value || "";
    setSearchQuery(searchValue);

    if (!searchValue.trim()) {
      setFilteredRows(data || []);
      return;
    }

    const q = searchValue.toLowerCase().trim();
    
    const filtered = data?.filter((row) => {
      // Convert brandcode to string for comparison if it's a number
      const brandcodeStr = row.brandcode?.toString() || "";
      
      return (
        row.brandname.toLowerCase().includes(q) ||
        brandcodeStr.toLowerCase().includes(q)
      );
    });
    
    setFilteredRows(filtered || []);
  };

  const addToView = (): void => {
    navigate("/CreateBrands");
  };

  // Handler for row updates (if needed)
  const handleProcessRowUpdate = async (newRow: any, oldRow: any): Promise<any> => {
    console.log("Row update attempted:", newRow, oldRow);
    
    // Show message that inline editing is not supported
    setAlertMessage(Messages.brand.warning.inlineEditNotSupported);
    setAlertType(MessageCategory.WARNING);
    setAlertOpen(true);
    
    return oldRow; // Return old row to revert any changes
  };

  // Handler for row selection (optional)
  const handleRowSelect = (selectedRows: any[]): void => {
    console.log("Selected rows:", selectedRows);
  };

  // Transform the data to ensure each row has an id property
  const mappedRows: ViewDataGridRow[] = (filteredRows || []).map((row) => ({
    ...row,
    id: row.brandcode || `row-${Math.random()}`,
  }));

  return (
    <>
      <Box sx={styles.box_breadcrumb_view}>
        <Breadcrumbs
          separator="›"
          aria-label="breadcrumb"
          sx={styles.breadcrumb}
        >
          <Link
            underline="hover"
            sx={styles.breadcrumb_link}
          >
            {UITextMessages.breadcrumb.home}
          </Link>
          <Link
            sx={styles.breadcrumb_link}
            underline="hover"
          >
            {UITextMessages.breadcrumb.settings}
          </Link>
          <Link
            sx={styles.breadcrumb_link_page}
            underline="hover"
            aria-current="page"
          >
            Brands
          </Link>
        </Breadcrumbs>
      </Box>

      <Box sx={styles.box_page_container}>
        <AppBar sx={styles.appbar}>
          <Grid 
            container 
            spacing={2} 
            sx={styles.appbar_grid_container}
          >
            <Grid
              size={{ xs: 12, sm: 12, md: 6, lg: 8, xl: 8 }}
              sx={styles.appbar_grid_container_col_1}
            >
              <Typography sx={styles.appbar_title}>{brandData.name}</Typography>
            </Grid>

            <Grid
              size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 4 }}
              sx={styles.appbar_grid_search}
            >
              <Autocomplete
                freeSolo
                id="act_appbarsearch"
                fullWidth
                disableClearable
                value={searchQuery}
                onInputChange={handleSearchChange}
                sx={styles.ipt_appbar_search_auto}
                options={data?.map((row) => row.brandname) || []}
                renderInput={(params) => {
                  const { id, ...restParams } = params;
                  return (
                    <TextField
                      id="txt_appbarsearch"
                      {...restParams}
                      placeholder={Messages.brand.ui.searchPlaceholder}
                      size="small"
                      sx={styles.ipt_appbar_search}
                      InputProps={{
                        ...restParams.InputProps,
                        type: "search",
                        endAdornment: (
                          <InputAdornment position="end">
                                          <IconButton
                                            id="btn_appbarsearch"
                                            sx={styles.btn_appbar_search_icon}
                                          >
                                            <SearchIcon id="ico_search" />
                                          </IconButton>
                                        </InputAdornment>
                        ),
                      }}
                    />
                  );
                }}
              />

              <Button
                id="btn_add"
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                size="small"
                sx={{ ...styles.btn_primary, ...styles.btn_appbar_add ,ml:1}}
                onClick={addToView}
                disabled={isLoading}
              >
                Add
              </Button>
            </Grid>
          </Grid>
        </AppBar>

        <Box sx={styles.view_page_container}>
          <ViewDataGrid
            columns={brandData.columns}
            rows={mappedRows}
            menuItems={menuItems}
            tabletitle={brandData.name}
            gridid="brandcode"
            processRowUpdate={handleProcessRowUpdate}
            onRowSelect={handleRowSelect}
          />
        </Box>
      </Box>

      <SystemAlert
        open={alertOpen}
        type={alertType}
        message={alertMessage}
        onClose={() => setAlertOpen(false)}
      />

      <Dialog
        id="dlg_delete"
        open={openDeleteDialog}
        onClose={cancelDelete}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the brand "{selectedItem?.brandname}"?
          </DialogContentText>
          {/* {selectedItem?.descrip && (
            <DialogContentText sx={{ mt: 2, fontStyle: 'italic' }}>
              Description: {selectedItem.descrip}
            </DialogContentText>
          )} */}
        </DialogContent>
        <DialogActions>
          <Button
            id="btn_cancel"
            onClick={cancelDelete}
            variant="outlined"
            sx={{ ...styles.btn_secondary, ...styles.btn_add_cancel }}
            // color="inherit"
            disabled={isLoading || isCheckingUsage}
          >
            {UITextMessages.button.cancel}
          </Button>
          <Button
            id="btn_confirm_delete"
            onClick={confirmDelete}
            color="error"
            sx={{ ...styles.btn_primary, ...styles.btn_add_confirm }}
            variant="contained"
            disabled={isLoading || isCheckingUsage}
          >
            {isLoading ? "Deleting..." : UITextMessages.button.delete}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ViewBrands;